import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { type ColumnDef } from '@tanstack/react-table';
import _ from 'lodash';
import { EllipsisVertical, PenIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { formatCurrencyValue } from '../../utils';

import { CashUseSection } from './CashUseSection';

import ComboboxField from '@/components/ComboBox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import NumberField from '@/components/NumberField';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyContent } from '@/components/ui/empty';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { formatNumber, formatPercent } from '@/lib/utils';

const SectionHeader = ({
  children,
  title
}: PropsWithChildren<{
  title: string;
}>) => (
  <div className="mb-2 flex h-9 items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const SECURITY_TYPE_LABELS: Record<string, string> = {
  capitalized_leases: 'Capitalized Leases',
  cash: 'Cash',
  common_equity: 'Common Equity',
  convertible_preferred_equity: 'Convertible Preferred Equity',
  deffered_consideration: 'Deferred Consideration',
  delayed_draw_term_loan: 'Delayed Draw Term Loan',
  earnout: 'Earnout',
  equipment_line: 'Equipment Line',
  first_lien_term_loan: 'First Lien Term Loan',
  first_lien_term_loan_first_out: 'First Lien Term Loan (First Out)',
  first_lien_term_loan_last_out: 'First Lien Term Loan (Last Out)',
  holdco_note: 'HoldCo Note',
  implied_equity: 'Implied Equity',
  management_incentive_plan: 'Management Incentive Plan',
  non_participating_preferred_equity: 'Non-Participating Preferred Equity',
  participating_preferred_equity: 'Participating Preferred Equity',
  redeemable_preferred_equity: 'Redeemable Preferred Equity',
  revolver: 'Revolver',
  rollover_equity: 'Rollover Equity',
  second_lien_term_loan: 'Second Lien Term Loan',
  seller_note: 'Seller Note',
  unsecured_note: 'Unsecured Note',
  warrants: 'Warrants'
};

const getSecurityTypeLabel = (key: null | string | undefined): string => {
  if (!key) return '-';
  return SECURITY_TYPE_LABELS[key] ?? _.startCase(key);
};

const getCategoryLabel = (
  slug: null | string | undefined,
  categories?: { name: string; value: string }[]
): string => {
  if (!slug) return '-';
  const match = categories?.find(c => c.value === slug);
  return match?.name ?? _.startCase(slug);
};

const getPartyTypeLabel = (
  slug: null | string | undefined,
  partyTypes?: { name: string; value: string }[]
): string => {
  if (!slug) return '-';
  const match = partyTypes?.find(pt => pt.value === slug);
  return match?.name ?? _.startCase(slug);
};

const TruncatedPillBadge = ({ text }: { text: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Badge className="inline-block max-w-[140px] truncate border-transparent bg-pink-200 text-pink-800">
        {text}
      </Badge>
    </TooltipTrigger>
    <TooltipContent>
      <p className="max-w-xs">{text}</p>
    </TooltipContent>
  </Tooltip>
);

interface GetDealOpportunitySecuritySummary {
  node: {
    computedSecuritySummary: null | SecuritySummaryItem[];
    id: string;
  };
}

interface SecuritySummaryItem {
  amount: null | number;
  category: string;
  id: string;
  netAttachmentPoint: string;
  netDetachmentPoint: string;
  percentOfTotal: number;
  security_type_name: string;
}

const GET_DEAL_OPPORTUNITY_CAPTABLE = gql`
  query DealOpportunity_CapTable($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        ltmEbitda
        referencePoint
        computedSecuritySummary
        computedSourcesUses
        computedProjectedDebtIrr
        computedProjectedDebtMoic
        computedProjectedEquityIrr
        computedProjectedEquityMoic
        computedProjectedTotalIrr
        computedProjectedTotalMoic
        projectedDebtIrr
        projectedDebtMoic
        projectedEquityIrr
        projectedEquityMoic
        projectedTotalIrr
        projectedTotalMoic
      }
    }
  }
`;

interface GetAccounts {
  searchAccounts: {
    __typename: 'AccountNode';
    dynamicsId: null | string;
    id: string;
    name: null | string;
    relationshipType: null | string;
  }[];
}

const GET_ACCOUNTS = gql`
  query Accounts($input: [String]) {
    searchAccounts(limit: 100, name: $input) {
      id
      dynamicsId
      name
      relationshipType
    }
  }
`;

interface GetPartyTypes {
  getFieldSelections: {
    __typename: 'ModelFieldSelectionNode';
    id: string;
    name: string;
    value: string;
  }[];
}

const GET_PARTY_TYPES = gql`
  query SelectionFields {
    getFieldSelections(
      model: "opstream.opportunity.security.party"
      fieldName: "party_type"
    ) {
      id
      name
      value
    }
  }
`;

interface FundingHistory {
  fundingAmount: null | number;
  fundingDate: null | string;
  id: string;
  undrawnAmount: null | number;
}

interface GetOpportunitySecurities {
  opportunitySecurities: {
    __typename: 'DealOpportunitySecurityNode';
    amortizationPrerannum: null | number;
    amount: null | number;
    baseRateFloor: null | number;
    baseRateType: null | string;
    callProtection: null | string;
    cashFlow: null | string;
    cashFlowSweep: null | number;
    cashFlowSweepDetails: null | string;
    cashNoncash: null | string;
    cashSpread: null | number;
    category: null | string;
    closingFee: null | number;
    costOfWarrants: null | number;
    covenants: null | string;
    debtTranche: null | string;
    description: null | string;
    hideFromCapTable: boolean | null;
    hideFromSources: boolean | null;
    id: string;
    leverageBasedPricing: null | number;
    lien: null | number;
    lienSplit: null | string;
    lienType: null | string;
    manualAmount: null | number;
    maturityMonths: null | number;
    maturityYears: null | number;
    name: null | string;
    opcoHoldco: null | string;
    ownershipPercentage: null | number;
    pikSpread: null | number;
    projectedIrr: null | number;
    projectedMoic: null | number;
    securedUnsecured: null | string;
    securityType: null | string;
    totalAmount: null | number;
    totalPikInterestRate: null | number;
    warrantsAttached: null | string;
  }[];
}

interface GetSecurityPartiesBySecurity {
  securityPartiesBySecurity: {
    drawnAmount: null | number;
    fdoPct: null | number;
    fundingHistory: FundingHistory[];
    id: string;
    name: null | string;
    partyType: null | string;
    syndicatedPartner: boolean | null;
    targetEntityId: null | string;
    undrawnAmount: null | number;
  }[];
}

type OpportunitySecurity =
  GetOpportunitySecurities['opportunitySecurities'][number];
type SecurityParty =
  GetSecurityPartiesBySecurity['securityPartiesBySecurity'][number];

const GET_OPPORTUNITY_SECURITIES = gql`
  query DOSecurity($deal_id: ID!) {
    opportunitySecurities(dealOpportunityId: $deal_id) {
      id
      name
      securityType
      category
      amount
      amortizationPrerannum
      baseRateFloor
      baseRateType
      callProtection
      cashFlow
      cashFlowSweep
      cashFlowSweepDetails
      cashNoncash
      cashSpread
      closingFee
      costOfWarrants
      covenants
      debtTranche
      description
      lien
      leverageBasedPricing
      lienSplit
      lienType
      manualAmount
      maturityMonths
      maturityYears
      opcoHoldco
      ownershipPercentage
      pikSpread
      projectedIrr
      projectedMoic
      securedUnsecured
      totalAmount
      totalPikInterestRate
      warrantsAttached
      hideFromCapTable
      hideFromSources
    }
  }
`;

const GET_SECURITY_PARTIES_BY_SECURITY = gql`
  query OpstreamOpportunitySecurityParty($security_id: ID!) {
    securityPartiesBySecurity(securityId: $security_id) {
      id
      targetEntityId
      name
      syndicatedPartner
      fdoPct
      drawnAmount
      undrawnAmount
      partyType
      fundingHistory {
        id
        fundingDate
        fundingAmount
        undrawnAmount
      }
    }
  }
`;

interface CreateSecurityPartyResult {
  createOpportunitySecurityParty: {
    ok: boolean;
    securityParty: { id: string } | null;
  };
}

const CREATE_SECURITY_PARTY = gql`
  mutation CreateSecurityParty(
    $target_entity_id: ID
    $target_entity_type: String
    $syndicated_partner: Boolean
    $party_type: String
    $display_name: String
    $security_id: ID
    $fdoPct: Float
  ) {
    createOpportunitySecurityParty(
      targetEntityId: $target_entity_id
      targetEntityType: $target_entity_type
      securityId: $security_id
      values: {
        syndicatedPartner: $syndicated_partner
        partyType: $party_type
        displayName: $display_name
        fdoPct: $fdoPct
      }
    ) {
      ok
      securityParty {
        id
      }
    }
  }
`;

const UPDATE_SECURITY_PARTY = gql`
  mutation UpdateSecurityParty(
    $id: ID!
    $syndicated_partner: Boolean
    $party_type: String
    $display_name: String
    $fdoPct: Float
  ) {
    updateOpportunitySecurityParty(
      id: $id
      values: {
        syndicatedPartner: $syndicated_partner
        partyType: $party_type
        displayName: $display_name
        fdoPct: $fdoPct
      }
    ) {
      ok
    }
  }
`;

const CREATE_OPSTREAM_DEAL_OPPORTUNITY_SECURITY = gql`
  mutation createOpstreamDealOpportunitySecurity(
    $description: String
    $category: String
    $security_type: String
    $lien: Int
    $base_rate_type: String
    $base_rate_floor: Float
    $net_attachment_point: String
    $net_dettachment_point: String
    $total_cash_interest_rate: Float
    $total_pik_interest_rate: Float
    $closing_fee: Float
    $ownership_percentage: Float
    $leverage_based_pricing: Float
    $equity_class: String
    $debt_tranche: String
    $maturity_years: Int
    $maturity_months: Int
    $cash_noncash: String
    $secured_unsecured: String
    $opco_holdco: String
    $call_protection: String
    $lien_split: String
    $lien_type: String
    $cash_spread: Float
    $pik_spread: Float
    $amortization_prerannum: Float
    $warrants_attached: String
    $cost_of_warrants: Float
    $projected_irr: Float
    $projected_moic: Float
    $cash_flow: String
    $liquidation_preference: String
    $deal_opportunity_id: ID!
    $total_amount: Float
    $price_per_share: Float
    $cash_flow_sweep: Float
    $cash_flow_sweep_details: String
    $covenants: String
    $unitranch_interest_rate: Float
    $implied_lo_rate: Float
    $manualAmount: Float
    $hideFromCapTable: Boolean
    $hideFromSources: Boolean
  ) {
    createDealOpportunitySecurity(
      values: {
        description: $description
        category: $category
        securityType: $security_type
        lien: $lien
        baseRateType: $base_rate_type
        baseRateFloor: $base_rate_floor
        netAttachmentPoint: $net_attachment_point
        netDettachmentPoint: $net_dettachment_point
        totalCashInterestRate: $total_cash_interest_rate
        totalPikInterestRate: $total_pik_interest_rate
        closingFee: $closing_fee
        ownershipPercentage: $ownership_percentage
        leverageBasedPricing: $leverage_based_pricing
        equityClass: $equity_class
        debtTranche: $debt_tranche
        maturityYears: $maturity_years
        maturityMonths: $maturity_months
        cashNoncash: $cash_noncash
        securedUnsecured: $secured_unsecured
        opcoHoldco: $opco_holdco
        callProtection: $call_protection
        lienSplit: $lien_split
        lienType: $lien_type
        cashSpread: $cash_spread
        pikSpread: $pik_spread
        amortizationPrerannum: $amortization_prerannum
        warrantsAttached: $warrants_attached
        costOfWarrants: $cost_of_warrants
        projectedIrr: $projected_irr
        projectedMoic: $projected_moic
        cashFlow: $cash_flow
        liquidationPreference: $liquidation_preference
        dealOpportunityId: $deal_opportunity_id
        totalAmount: $total_amount
        pricePerShare: $price_per_share
        cashFlowSweep: $cash_flow_sweep
        cashFlowSweepDetails: $cash_flow_sweep_details
        unitranchInterestRate: $unitranch_interest_rate
        covenants: $covenants
        impliedLoRate: $implied_lo_rate
        manualAmount: $manualAmount
        hideFromCapTable: $hideFromCapTable
        hideFromSources: $hideFromSources
      }
    ) {
      ok
    }
  }
`;

const UPDATE_OPSTREAM_DEAL_OPPORTUNITY_SECURITY = gql`
  mutation updateOpstreamDealOpportunitySecurity(
    $security_id: ID!
    $description: String
    $category: String
    $security_type: String
    $lien: Int
    $base_rate_type: String
    $base_rate_floor: Float
    $net_attachment_point: String
    $net_dettachment_point: String
    $total_cash_interest_rate: Float
    $total_pik_interest_rate: Float
    $closing_fee: Float
    $ownership_percentage: Float
    $leverage_based_pricing: Float
    $equity_class: String
    $debt_tranche: String
    $maturity_years: Int
    $maturity_months: Int
    $cash_noncash: String
    $secured_unsecured: String
    $opco_holdco: String
    $call_protection: String
    $lien_split: String
    $lien_type: String
    $cash_spread: Float
    $pik_spread: Float
    $amortization_prerannum: Float
    $warrants_attached: String
    $cost_of_warrants: Float
    $projected_irr: Float
    $projected_moic: Float
    $cash_flow: String
    $liquidation_preference: String
    $total_amount: Float
    $price_per_share: Float
    $cash_flow_sweep: Float
    $cash_flow_sweep_details: String
    $covenants: String
    $unitranch_interest_rate: Float
    $implied_lo_rate: Float
    $manualAmount: Float
    $hideFromCapTable: Boolean
    $hideFromSources: Boolean
  ) {
    updateDealOpportunitySecurity(
      id: $security_id
      values: {
        description: $description
        category: $category
        securityType: $security_type
        lien: $lien
        baseRateType: $base_rate_type
        baseRateFloor: $base_rate_floor
        netAttachmentPoint: $net_attachment_point
        netDettachmentPoint: $net_dettachment_point
        totalCashInterestRate: $total_cash_interest_rate
        totalPikInterestRate: $total_pik_interest_rate
        closingFee: $closing_fee
        ownershipPercentage: $ownership_percentage
        leverageBasedPricing: $leverage_based_pricing
        equityClass: $equity_class
        debtTranche: $debt_tranche
        maturityYears: $maturity_years
        maturityMonths: $maturity_months
        cashNoncash: $cash_noncash
        securedUnsecured: $secured_unsecured
        opcoHoldco: $opco_holdco
        callProtection: $call_protection
        lienSplit: $lien_split
        lienType: $lien_type
        cashSpread: $cash_spread
        pikSpread: $pik_spread
        amortizationPrerannum: $amortization_prerannum
        warrantsAttached: $warrants_attached
        costOfWarrants: $cost_of_warrants
        projectedIrr: $projected_irr
        projectedMoic: $projected_moic
        cashFlow: $cash_flow
        liquidationPreference: $liquidation_preference
        totalAmount: $total_amount
        pricePerShare: $price_per_share
        cashFlowSweep: $cash_flow_sweep
        cashFlowSweepDetails: $cash_flow_sweep_details
        unitranchInterestRate: $unitranch_interest_rate
        covenants: $covenants
        impliedLoRate: $implied_lo_rate
        manualAmount: $manualAmount
        hideFromCapTable: $hideFromCapTable
        hideFromSources: $hideFromSources
      }
    ) {
      ok
    }
  }
`;

interface GetSecuritySelectionFields {
  baseRateTypes: SecuritySelectionField[];
  cashFlows: SecuritySelectionField[];
  cashNoncashes: SecuritySelectionField[];
  debtTranches: SecuritySelectionField[];
  lienSplits: SecuritySelectionField[];
  lienTypes: SecuritySelectionField[];
  opcoHoldcos: SecuritySelectionField[];
  securedUnsecureds: SecuritySelectionField[];
  securityCategories: SecuritySelectionField[];
  securityTypes: SecuritySelectionField[];
}

interface SecuritySelectionField {
  __typename: 'ModelFieldSelectionNode';
  id: string;
  name: string;
  value: string;
}

const GET_SECURITY_SELECTION_FIELDS = gql`
  query SecuritySelectionFields {
    securityCategories: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "category"
    ) {
      id
      name
      value
    }
    securityTypes: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "security_type"
    ) {
      id
      name
      value
    }
    baseRateTypes: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "base_rate_type"
    ) {
      id
      name
      value
    }
    opcoHoldcos: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "opco_holdco"
    ) {
      id
      name
      value
    }
    cashFlows: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "cash_flow"
    ) {
      id
      name
      value
    }
    securedUnsecureds: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "secured_unsecured"
    ) {
      id
      name
      value
    }
    lienSplits: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "lien_split"
    ) {
      id
      name
      value
    }
    lienTypes: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "lien_type"
    ) {
      id
      name
      value
    }
    debtTranches: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "debt_tranche"
    ) {
      id
      name
      value
    }
    cashNoncashes: getFieldSelections(
      model: "opstream.deal.opportunity.security"
      fieldName: "cash_noncash"
    ) {
      id
      name
      value
    }
  }
`;

const DELETE_SECURITY_PARTY = gql`
  mutation DeleteSecurityParty($id: ID!) {
    deleteOpportunitySecurityParty(id: $id) {
      ok
    }
  }
`;

const CREATE_PARTY_FUNDING = gql`
  mutation CreateSecurityPartyFunding($values: SecurityPartyFundingInput!) {
    createSecurityPartyFunding(values: $values) {
      ok
      funding {
        id
        fundingDate
        fundingAmount
        undrawnAmount
      }
    }
  }
`;

const DELETE_PARTY_FUNDING = gql`
  mutation DeleteSecurityPartyFunding($id: ID!) {
    deleteSecurityPartyFunding(id: $id) {
      ok
    }
  }
`;

export interface SecurityProps {
  id: string;
  onCashUseTotalChange?: (total: null | number) => void;
  onSecurityAdded?: () => Promise<unknown> | undefined;
}

interface TempFunding {
  fundingAmount: number;
  fundingDate: string;
  id: string;
  undrawnAmount: number;
}

export function Security({
  id,
  onCashUseTotalChange,
  onSecurityAdded
}: SecurityProps) {
  const { data: capTableData, loading: loadingCapTable } = useQuery<
    GetDealOpportunitySecuritySummary,
    { id: string }
  >(GET_DEAL_OPPORTUNITY_CAPTABLE, { variables: { id } });

  const { data: data2, loading: loading2 } = useQuery<
    GetOpportunitySecurities,
    { deal_id: string }
  >(GET_OPPORTUNITY_SECURITIES, { variables: { deal_id: id } });

  const { data: data5, loading: loading5 } = useQuery<
    GetAccounts,
    { input: null | string[] }
  >(GET_ACCOUNTS, { variables: { input: null } });

  const { data: data6, loading: loading6 } = useQuery<GetPartyTypes>(
    GET_PARTY_TYPES,
    { variables: {} }
  );

  const { data: data7, loading: loading7 } =
    useQuery<GetSecuritySelectionFields>(GET_SECURITY_SELECTION_FIELDS);

  const [selectedSecurityId, setSelectedSecurityId] = useState<null | string>(
    null
  );

  useEffect(() => {
    if (
      data2?.opportunitySecurities.length &&
      !selectedSecurityId &&
      data2.opportunitySecurities[0]?.id
    ) {
      setSelectedSecurityId(data2.opportunitySecurities[0].id);
    }
  }, [data2?.opportunitySecurities, selectedSecurityId]);

  const selectedSecurity = useMemo(
    () =>
      data2?.opportunitySecurities.find(s => s.id === selectedSecurityId) ??
      null,
    [data2?.opportunitySecurities, selectedSecurityId]
  );
  const isSecurityTypeSelected = selectedSecurity !== null;

  const { data: data3, loading: loading3 } = useQuery<
    GetSecurityPartiesBySecurity,
    { security_id: string }
  >(GET_SECURITY_PARTIES_BY_SECURITY, {
    skip: !selectedSecurityId,
    variables: { security_id: selectedSecurityId ?? '' }
  });

  const currentSecurityType = selectedSecurity?.securityType;

  const [createSecurityParty, { loading: isCreatingSecurityParty }] =
    useMutation<CreateSecurityPartyResult>(CREATE_SECURITY_PARTY);
  const [updateSecurityParty, { loading: isUpdatingSecurityParty }] =
    useMutation(UPDATE_SECURITY_PARTY);
  const [
    createOpstreamDealOpportunitySecurity,
    { loading: isCreatingOpstreamDealOpportunitySecurity }
  ] = useMutation(CREATE_OPSTREAM_DEAL_OPPORTUNITY_SECURITY);
  const [
    updateOpstreamDealOpportunitySecurity,
    { loading: isUpdatingOpstreamDealOpportunitySecurity }
  ] = useMutation(UPDATE_OPSTREAM_DEAL_OPPORTUNITY_SECURITY);
  const [deleteSecurityParty, { loading: isDeletingSecurityParty }] =
    useMutation(DELETE_SECURITY_PARTY);
  const [createPartyFunding, { loading: isCreatingPartyFunding }] =
    useMutation(CREATE_PARTY_FUNDING);
  const [deletePartyFunding, { loading: isDeletingPartyFunding }] =
    useMutation(DELETE_PARTY_FUNDING);

  const [createPartyDialogIsOpen, setCreatePartyDialogIsOpen] = useState(false);
  const [editPartyDialogIsOpen, setEditPartyDialogIsOpen] = useState(false);
  const [securityPartyBeingEdited, setSecurityPartyBeingEdited] =
    useState<null | SecurityParty>(null);
  const [securityPartyDeleteDialogIsOpen, setSecurityPartyDeleteDialogIsOpen] =
    useState(false);
  const [securityPartyBeingDeleted, setSecurityPartyBeingDeleted] =
    useState<null | SecurityParty>(null);
  const [securityTypeDialogIsOpen, setSecurityTypeDialogIsOpen] =
    useState(false);
  const [securityTypeBeingEdited, setSecurityTypeBeingEdited] =
    useState<null | OpportunitySecurity>(null);
  const [warrantsAttached, setWarrantsAttached] = useState(false);
  const [hideFromCapTable, setHideFromCapTable] = useState(false);
  const [hideFromSources, setHideFromSources] = useState(false);
  const [securityCategoryFieldIsInvalid, setSecurityCategoryFieldIsInvalid] =
    useState(false);
  const [securityTypeFieldIsInvalid, setSecurityTypeFieldIsInvalid] =
    useState(false);
  const [selectedSecurityCategory, setSelectedSecurityCategory] = useState<
    null | string
  >('debt');
  const [createPartyFundings, setCreatePartyFundings] = useState<TempFunding[]>(
    []
  );
  const [addFundingDialogIsOpen, setAddFundingDialogIsOpen] = useState(false);
  const [editAddFundingDialogIsOpen, setEditAddFundingDialogIsOpen] =
    useState(false);

  useEffect(() => {
    if (!editPartyDialogIsOpen) setSecurityPartyBeingEdited(null);
  }, [editPartyDialogIsOpen]);
  useEffect(() => {
    if (securityPartyBeingEdited && data3?.securityPartiesBySecurity) {
      const updatedParty = data3.securityPartiesBySecurity.find(
        p => p.id === securityPartyBeingEdited.id
      );
      if (updatedParty && updatedParty !== securityPartyBeingEdited) {
        setSecurityPartyBeingEdited(updatedParty);
      }
    }
  }, [data3?.securityPartiesBySecurity, securityPartyBeingEdited]);
  useEffect(() => {
    if (!securityPartyDeleteDialogIsOpen) setSecurityPartyBeingDeleted(null);
  }, [securityPartyDeleteDialogIsOpen]);
  useEffect(() => {
    if (!createPartyDialogIsOpen) setCreatePartyFundings([]);
  }, [createPartyDialogIsOpen]);

  const shouldShowUndrawnAmount = useMemo(() => {
    if (!currentSecurityType) return false;
    const type = currentSecurityType.toLowerCase();
    return type === 'delayed_draw_term_loan' || type === 'revolver';
  }, [currentSecurityType]);

  const totalDrawnForSecurity = useMemo(() => {
    if (!data3?.securityPartiesBySecurity) return 0;
    return data3.securityPartiesBySecurity.reduce(
      (total, party) => total + (party.drawnAmount ?? 0),
      0
    );
  }, [data3?.securityPartiesBySecurity]);

  const remainingCommitment = useMemo(() => {
    const commitmentSize = selectedSecurity?.totalAmount ?? 0;
    return commitmentSize - totalDrawnForSecurity;
  }, [selectedSecurity?.totalAmount, totalDrawnForSecurity]);

  const securityTypeColumns = useMemo<ColumnDef<OpportunitySecurity>[]>(
    () => [
      {
        accessorKey: 'securityType',
        cell: ({ row }) => (
          <TruncatedPillBadge
            text={getSecurityTypeLabel(row.original.securityType)}
          />
        ),
        header: () => (
          <div className="text-xs font-semibold">Security Type</div>
        ),
        size: 180
      },
      {
        accessorKey: 'category',
        cell: ({ row }) => (
          <TruncatedPillBadge
            text={getCategoryLabel(
              row.original.category,
              data7?.securityCategories
            )}
          />
        ),
        header: () => <div className="text-xs font-semibold">Category</div>,
        size: 140
      },
      {
        accessorKey: 'totalAmount',
        cell: ({ row }) => (
          <div className="text-right text-sm font-medium">
            {row.original.totalAmount != null
              ? formatCurrencyValue(row.original.totalAmount)
              : null}
          </div>
        ),
        header: () => (
          <div className="text-right text-xs font-semibold">
            Commitment Size
          </div>
        ),
        size: 140
      },
      {
        accessorKey: 'lien',
        cell: ({ row }) => row.original.lien,
        header: () => <div className="text-xs font-semibold">Lien</div>,
        size: 80
      },
      {
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="size-8"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <EllipsisVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => {
                    setSecurityTypeBeingEdited(row.original);
                    setSelectedSecurityCategory(row.original.category);
                    setWarrantsAttached(
                      String(row.original.warrantsAttached).toLowerCase() ===
                        'true'
                    );
                    setHideFromCapTable(row.original.hideFromCapTable ?? false);
                    setHideFromSources(row.original.hideFromSources ?? false);
                    setSecurityTypeDialogIsOpen(true);
                  }}
                >
                  <PenIcon />
                  Edit
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
        header: () => null,
        id: 'actions',
        size: 48
      }
    ],
    [data7?.securityCategories]
  );

  const handleSecurityTypeFormSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const getString = (key: string) => {
      const value = formData.get(key);
      if (typeof value !== 'string') return null;
      const trimmed = value.trim();
      return trimmed === '' ? null : trimmed;
    };
    const getFloat = (key: string) => {
      const value = getString(key);
      if (!value) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };
    const getInt = (key: string) => {
      const value = getString(key);
      if (!value) return null;
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : null;
    };
    const pctToDecimal = (v: null | number) => (v == null ? null : v / 100);
    const category = getString('security-category-field');
    const securityType = getString('security-type-field');
    const isCashCategory = category === 'cash';
    let formIsInvalid = false;
    if (!category) {
      setSecurityCategoryFieldIsInvalid(true);
      formIsInvalid = true;
    }
    if (!isCashCategory && !securityType) {
      setSecurityTypeFieldIsInvalid(true);
      formIsInvalid = true;
    }
    if (formIsInvalid) return;
    try {
      const commonVariables = {
        amortization_prerannum:
          category === 'equity'
            ? null
            : pctToDecimal(getFloat('amortization-per-annum-field')),
        base_rate_floor: pctToDecimal(getFloat('base-rate-floor-field')),
        base_rate_type: getString('base-rate-type-field'),
        call_protection: getString('call-protection-field'),
        cash_flow: getString('cash-flow-field'),
        cash_flow_sweep: pctToDecimal(getFloat('cash-flow-sweep-field')),
        cash_flow_sweep_details: getString('cash-flow-sweep-details-field'),
        cash_noncash: getString('cash-non-cash-field'),
        cash_spread: pctToDecimal(getFloat('cash-spread-field')),
        category,
        closing_fee: pctToDecimal(getFloat('closing-fee-field')),
        cost_of_warrants: pctToDecimal(getFloat('cost-of-warrants-field')),
        covenants: getString('covenants-field'),
        debt_tranche: getString('debt-tranche-field'),
        description: getString('security-description-field'),
        hideFromCapTable,
        hideFromSources,
        implied_lo_rate: pctToDecimal(getFloat('implied-lo-rate-field')),
        leverage_based_pricing:
          category === 'equity'
            ? null
            : getFloat('leverage-based-pricing-field'),
        lien: getInt('lien-field'),
        lien_split:
          category === 'equity' ? null : getString('lien-split-field'),
        lien_type:
          category === 'unsecured_debt' ? getString('lien-type-field') : null,
        manualAmount: isCashCategory ? getFloat('manual-amount-field') : null,
        maturity_months: getInt('maturity-months-field'),
        maturity_years: getInt('maturity-years-field'),
        opco_holdco: getString('opco-holdco-field'),
        ownership_percentage:
          category === 'equity'
            ? pctToDecimal(getFloat('ownership-pct-field'))
            : null,
        pik_spread: pctToDecimal(getFloat('pik-spread-field')),
        projected_irr: pctToDecimal(getFloat('projected-irr-field')),
        projected_moic: getFloat('projected-moic-field'),
        secured_unsecured: getString('secured-unsecured-field'),
        security_type: isCashCategory ? 'cash' : securityType,
        total_amount: getFloat('commitment-size-field'),
        total_pik_interest_rate: pctToDecimal(
          getFloat('total-pik-interest-rate-field')
        ),
        unitranch_interest_rate: pctToDecimal(
          getFloat('unitranche-interest-rate-field')
        ),
        warrants_attached: warrantsAttached ? 'true' : 'false'
      };
      const { error } = securityTypeBeingEdited
        ? await updateOpstreamDealOpportunitySecurity({
            refetchQueries: [
              { query: GET_OPPORTUNITY_SECURITIES, variables: { deal_id: id } },
              { query: GET_DEAL_OPPORTUNITY_CAPTABLE, variables: { id } }
            ],
            variables: {
              security_id: securityTypeBeingEdited.id,
              ...commonVariables
            }
          })
        : await createOpstreamDealOpportunitySecurity({
            refetchQueries: [
              { query: GET_OPPORTUNITY_SECURITIES, variables: { deal_id: id } },
              { query: GET_DEAL_OPPORTUNITY_CAPTABLE, variables: { id } }
            ],
            variables: {
              deal_opportunity_id: id,
              ...commonVariables
            }
          });
      if (error) throw new Error('An error has occurred');
      if (!securityTypeBeingEdited) {
        await onSecurityAdded?.();
      }
      setWarrantsAttached(false);
      setHideFromCapTable(false);
      setHideFromSources(false);
      setSecurityTypeBeingEdited(null);
      setSecurityTypeDialogIsOpen(false);
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message);
    }
  };

  const handleCreatePartyForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const party = formData.get('party-field');
    const displayName = formData.get('display-name-field');
    const ownershipPct = formData.get('ownership-pct-field');
    const syndicatedPartner = formData.get('syndicated-partner-field');
    const partyType = formData.get('party-type-field');
    const partyId = typeof party === 'string' && party !== '' ? party : null;
    const partyTypeValue =
      typeof partyType === 'string' && partyType !== '' ? partyType : null;
    try {
      const { data, error } = await createSecurityParty({
        refetchQueries: [
          {
            query: GET_SECURITY_PARTIES_BY_SECURITY,
            variables: { security_id: selectedSecurityId }
          }
        ],
        variables: {
          display_name: displayName === '' ? null : displayName,
          fdoPct: ownershipPct ? +ownershipPct : null,
          party_type: partyTypeValue,
          security_id: selectedSecurityId,
          syndicated_partner: syndicatedPartner === 'on',
          target_entity_id: partyId,
          target_entity_type: 'opstream.account'
        }
      });
      if (error) throw new Error('An error has occurred');
      const securityPartyId =
        data?.createOpportunitySecurityParty.securityParty?.id;
      if (securityPartyId && createPartyFundings.length > 0) {
        for (let i = 0; i < createPartyFundings.length; i++) {
          const funding = createPartyFundings[i];
          const isLast = i === createPartyFundings.length - 1;
          await createPartyFunding({
            refetchQueries: isLast
              ? [
                  {
                    query: GET_SECURITY_PARTIES_BY_SECURITY,
                    variables: { security_id: selectedSecurityId }
                  },
                  {
                    query: GET_OPPORTUNITY_SECURITIES,
                    variables: { deal_id: id }
                  }
                ]
              : [],
            variables: {
              values: {
                fundingAmount: funding.fundingAmount,
                fundingDate: funding.fundingDate,
                securityPartyId,
                undrawnAmount: funding.undrawnAmount
              }
            }
          });
        }
        toast.success(
          `Security party created with ${createPartyFundings.length.toString()} funding(s)`
        );
      } else {
        toast.success('Security party created successfully');
      }
      setCreatePartyFundings([]);
      setCreatePartyDialogIsOpen(false);
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message);
    }
  };

  const handleEditPartyForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!securityPartyBeingEdited) return;
    const formData = new FormData(e.currentTarget);
    const displayName = formData.get('display-name-field');
    const ownershipPct = formData.get('ownership-pct-field');
    const syndicatedPartner = formData.get('syndicated-partner-field');
    const partyType = formData.get('party-type-field');
    const partyTypeValue =
      typeof partyType === 'string' && partyType !== '' ? partyType : null;
    try {
      const { error } = await updateSecurityParty({
        refetchQueries: [
          {
            query: GET_SECURITY_PARTIES_BY_SECURITY,
            variables: { security_id: selectedSecurityId }
          }
        ],
        variables: {
          display_name: displayName === '' ? null : displayName,
          fdoPct: ownershipPct ? +ownershipPct : null,
          id: securityPartyBeingEdited.id,
          party_type: partyTypeValue,
          syndicated_partner: syndicatedPartner === 'on'
        }
      });
      if (error) throw new Error('An error has occurred');
      setEditPartyDialogIsOpen(false);
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message);
    }
  };

  const handleDeleteSecurityParty = async (partyId: string) => {
    try {
      const { error } = await deleteSecurityParty({
        refetchQueries: [
          {
            query: GET_SECURITY_PARTIES_BY_SECURITY,
            variables: { security_id: selectedSecurityId }
          }
        ],
        variables: { id: partyId }
      });
      if (error) throw new Error('An error has occurred');
    } catch (err) {
      console.error(err);
      toast.error((err as Error).message);
    }
  };

  const handleConfirmDeleteSecurityParty = async () => {
    if (!securityPartyBeingDeleted) return;
    await handleDeleteSecurityParty(securityPartyBeingDeleted.id);
    setSecurityPartyDeleteDialogIsOpen(false);
  };

  const loading =
    loadingCapTable || loading2 || loading5 || loading6 || loading7;
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="size-6 text-blue-500" />
      </div>
    );
  }
  if (!capTableData || !data2 || !data5 || !data6 || !data7) {
    return <div>{'Error loading security data'}</div>;
  }

  const computedSecuritySummary =
    capTableData.node.computedSecuritySummary ?? [];

  return (
    <>
      <div className="space-y-2">
        <SectionHeader title="Security" />
        <div className="bg-card border-border rounded-md border p-4">
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-semibold">
                  Security
                </TableHead>
                <TableHead className="text-right text-xs font-semibold">
                  At Close Structure
                </TableHead>
                <TableHead className="text-right text-xs font-semibold">
                  % of Capital
                </TableHead>
                <TableHead className="text-right text-xs font-semibold">
                  Attachement
                </TableHead>
                <TableHead className="text-right text-xs font-semibold">
                  Detachment
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {computedSecuritySummary.map(a => (
                <TableRow key={a.id}>
                  <TableCell className="text-sm font-medium">
                    {a.security_type_name}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {a.amount !== null ? formatCurrencyValue(a.amount) : ''}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {formatPercent(a.percentOfTotal, { isDecimal: true })}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {formatNumber(a.netAttachmentPoint)}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {formatNumber(a.netDetachmentPoint)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="grid gap-4 @md:grid-cols-2">
        <div className="min-w-0 space-y-2">
          <SectionHeader title="Security Type">
            <Dialog
              onOpenChange={(open: boolean) => {
                setSecurityTypeDialogIsOpen(open);
                if (!open) {
                  setWarrantsAttached(false);
                  setHideFromCapTable(false);
                  setHideFromSources(false);
                  setSecurityTypeBeingEdited(null);
                  setSecurityCategoryFieldIsInvalid(false);
                  setSecurityTypeFieldIsInvalid(false);
                  setSelectedSecurityCategory('debt');
                }
              }}
              open={securityTypeDialogIsOpen}
            >
              {data2.opportunitySecurities.length !== 0 && (
                <DialogTrigger asChild>
                  <Button>
                    <PlusIcon />
                    Add Row
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent aria-describedby={undefined}>
                <DialogHeader>
                  <DialogTitle>
                    {securityTypeBeingEdited
                      ? 'Edit Security Type'
                      : 'Security Type'}
                    <Badge variant="yellow">Unsaved</Badge>
                  </DialogTitle>
                </DialogHeader>
                <DialogBody>
                  <form
                    id="security-type-form"
                    noValidate
                    onSubmit={e => void handleSecurityTypeFormSubmit(e)}
                  >
                    <FieldGroup>
                      <FieldSet>
                        <FieldLegend>Required Fields</FieldLegend>
                        <FieldGroup>
                          <div className="grid grid-cols-3 gap-x-2.5">
                            <ComboboxField
                              allowDeselect={false}
                              defaultValue={
                                securityTypeBeingEdited?.category ?? 'debt'
                              }
                              error={
                                securityCategoryFieldIsInvalid
                                  ? 'Please select a value'
                                  : undefined
                              }
                              id="security-category-field"
                              isRequired
                              label="Security Category"
                              name="security-category-field"
                              onValueChange={value => {
                                setSelectedSecurityCategory(value);
                                if (securityCategoryFieldIsInvalid)
                                  setSecurityCategoryFieldIsInvalid(false);
                              }}
                              options={data7.securityCategories.map(c => ({
                                label: c.name,
                                value: c.value
                              }))}
                              placeholder="Select security category..."
                            />
                            {selectedSecurityCategory === 'cash' ? (
                              <Field>
                                <FieldLabel htmlFor="manual-amount-field">
                                  Manual Amount
                                </FieldLabel>
                                <NumberField
                                  defaultValue={
                                    securityTypeBeingEdited?.manualAmount ??
                                    undefined
                                  }
                                  id="manual-amount-field"
                                  name="manual-amount-field"
                                  placeholder="Enter Value"
                                />
                              </Field>
                            ) : (
                              <ComboboxField
                                defaultValue={
                                  securityTypeBeingEdited?.securityType ??
                                  undefined
                                }
                                error={
                                  securityTypeFieldIsInvalid
                                    ? 'Please select a value'
                                    : undefined
                                }
                                id="security-type-field"
                                isRequired
                                label="Security Type"
                                name="security-type-field"
                                onValueChange={() => {
                                  if (securityTypeFieldIsInvalid)
                                    setSecurityTypeFieldIsInvalid(false);
                                }}
                                options={data7.securityTypes.map(st => ({
                                  label: st.name,
                                  value: st.value
                                }))}
                                placeholder="Select security type..."
                              />
                            )}
                            <Field>
                              <FieldLabel htmlFor="lien-field">Lien</FieldLabel>
                              <NumberField
                                defaultValue={
                                  securityTypeBeingEdited?.lien ?? undefined
                                }
                                id="lien-field"
                                max={100}
                                min={0}
                                name="lien-field"
                                placeholder="Enter Value"
                              />
                            </Field>
                          </div>
                          <div className="grid grid-cols-2 gap-x-2.5">
                            <Field orientation="horizontal">
                              <FieldLabel htmlFor="hide-from-cap-table-field">
                                Hide from Cap Table
                              </FieldLabel>
                              <Switch
                                checked={hideFromCapTable}
                                id="hide-from-cap-table-field"
                                onCheckedChange={setHideFromCapTable}
                              />
                            </Field>
                            <Field orientation="horizontal">
                              <FieldLabel htmlFor="hide-from-sources-field">
                                Hide from Sources
                              </FieldLabel>
                              <Switch
                                checked={hideFromSources}
                                id="hide-from-sources-field"
                                onCheckedChange={setHideFromSources}
                              />
                            </Field>
                          </div>
                          {selectedSecurityCategory !== 'cash' && (
                            <Field>
                              <FieldLabel htmlFor="commitment-size-field">
                                Commitment Size
                              </FieldLabel>
                              <InputGroup>
                                <InputGroupAddon>
                                  <InputGroupText>$</InputGroupText>
                                </InputGroupAddon>
                                <NumberField
                                  defaultValue={
                                    securityTypeBeingEdited?.totalAmount ??
                                    undefined
                                  }
                                  id="commitment-size-field"
                                  name="commitment-size-field"
                                  placeholder="Enter Value"
                                />
                              </InputGroup>
                            </Field>
                          )}
                        </FieldGroup>
                      </FieldSet>
                      {selectedSecurityCategory !== 'cash' && (
                        <>
                          <FieldSeparator />
                          <FieldSet>
                            <FieldLegend>Refined Screening Metrics</FieldLegend>
                            <FieldGroup>
                              <div className="grid grid-cols-3 gap-x-2.5">
                                <Field>
                                  <FieldLabel htmlFor="base-rate-floor-field">
                                    Base Rate Floor
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.baseRateFloor ??
                                        undefined
                                      }
                                      id="base-rate-floor-field"
                                      max={100}
                                      min={0}
                                      name="base-rate-floor-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="closing-fee-field">
                                    Closing Fee
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.closingFee ??
                                        undefined
                                      }
                                      id="closing-fee-field"
                                      max={100}
                                      name="closing-fee-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                <Field>
                                  <FieldLabel htmlFor="total-pik-interest-rate-field">
                                    Total PIK Interest Rate
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.totalPikInterestRate ??
                                        undefined
                                      }
                                      id="total-pik-interest-rate-field"
                                      max={100}
                                      min={0}
                                      name="total-pik-interest-rate-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                              </div>
                              <div className="grid grid-cols-3 gap-x-2.5">
                                <ComboboxField
                                  defaultValue={
                                    securityTypeBeingEdited?.baseRateType ??
                                    undefined
                                  }
                                  id="base-rate-type-field"
                                  label="Base Rate Type"
                                  name="base-rate-type-field"
                                  options={data7.baseRateTypes.map(bt => ({
                                    label: bt.name,
                                    value: bt.value
                                  }))}
                                  placeholder="Select base rate type..."
                                />
                                <Field>
                                  <FieldLabel htmlFor="cash-spread-field">
                                    Cash Spread
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.cashSpread ??
                                        undefined
                                      }
                                      id="cash-spread-field"
                                      max={100}
                                      name="cash-spread-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                {selectedSecurityCategory === 'equity' ? (
                                  <Field>
                                    <FieldLabel htmlFor="ownership-pct-field">
                                      Ownership PCT
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.ownershipPercentage ??
                                        undefined
                                      }
                                      id="ownership-pct-field"
                                      name="ownership-pct-field"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                ) : (
                                  <Field>
                                    <FieldLabel htmlFor="leverage-based-pricing-field">
                                      Leverage Based Pricing
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.leverageBasedPricing ??
                                        undefined
                                      }
                                      id="leverage-based-pricing-field"
                                      name="leverage-based-pricing-field"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                )}
                              </div>
                            </FieldGroup>
                          </FieldSet>
                          <FieldSeparator />
                          <FieldSet>
                            <FieldLegend>All Other Fields</FieldLegend>
                            <FieldGroup>
                              <div className="grid grid-cols-3 gap-x-2.5 gap-y-2.5">
                                <Field>
                                  <FieldLabel htmlFor="projected-irr-field">
                                    Projected IRR
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.projectedIrr ??
                                        undefined
                                      }
                                      id="projected-irr-field"
                                      max={100}
                                      min={0}
                                      name="projected-irr-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                <div className="grid grid-cols-2 gap-x-2.5">
                                  <Field>
                                    <FieldLabel htmlFor="maturity-years-field">
                                      Maturity Years
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.maturityYears ??
                                        undefined
                                      }
                                      id="maturity-years-field"
                                      name="maturity-years-field"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                  <Field>
                                    <FieldLabel htmlFor="maturity-months-field">
                                      Maturity Months
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.maturityMonths ??
                                        undefined
                                      }
                                      id="maturity-months-field"
                                      name="maturity-months-field"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                </div>
                                <Field>
                                  <FieldLabel htmlFor="call-protection-field">
                                    Call Protection
                                  </FieldLabel>
                                  <Input
                                    defaultValue={
                                      securityTypeBeingEdited?.callProtection ??
                                      undefined
                                    }
                                    id="call-protection-field"
                                    name="call-protection-field"
                                    placeholder="Enter Value"
                                  />
                                </Field>
                              </div>
                              <div className="grid grid-cols-3 gap-x-2.5 gap-y-2.5">
                                <Field>
                                  <FieldLabel htmlFor="projected-moic-field">
                                    Projected MOIC
                                  </FieldLabel>
                                  <NumberField
                                    defaultValue={
                                      securityTypeBeingEdited?.projectedMoic ??
                                      undefined
                                    }
                                    id="projected-moic-field"
                                    name="projected-moic-field"
                                    placeholder="Enter Value"
                                  />
                                </Field>
                                {selectedSecurityCategory !== 'equity' && (
                                  <Field>
                                    <FieldLabel htmlFor="amortization-per-annum-field">
                                      Amortization per Annum
                                    </FieldLabel>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.amortizationPrerannum ??
                                        undefined
                                      }
                                      id="amortization-per-annum-field"
                                      name="amortization-per-annum-field"
                                      placeholder="Enter Value"
                                    />
                                  </Field>
                                )}
                                <ComboboxField
                                  defaultValue={
                                    securityTypeBeingEdited?.opcoHoldco ??
                                    undefined
                                  }
                                  id="opco-holdco-field"
                                  label="Opco Holdco"
                                  name="opco-holdco-field"
                                  options={data7.opcoHoldcos.map(oh => ({
                                    label: oh.name,
                                    value: oh.value
                                  }))}
                                  placeholder="Select opco/holdco..."
                                />
                              </div>
                              <div className="grid grid-cols-3 gap-x-2.5 gap-y-2.5">
                                <ComboboxField
                                  defaultValue={
                                    securityTypeBeingEdited?.cashFlow ??
                                    undefined
                                  }
                                  id="cash-flow-field"
                                  label="Cash Flow"
                                  name="cash-flow-field"
                                  options={data7.cashFlows.map(cf => ({
                                    label: cf.name,
                                    value: cf.value
                                  }))}
                                  placeholder="Select cash flow..."
                                />
                                <Field>
                                  <FieldLabel htmlFor="pik-spread-field">
                                    PIK Spread
                                  </FieldLabel>
                                  <NumberField
                                    defaultValue={
                                      securityTypeBeingEdited?.pikSpread ??
                                      undefined
                                    }
                                    id="pik-spread-field"
                                    name="pik-spread-field"
                                    placeholder="Enter Value"
                                  />
                                </Field>
                                <ComboboxField
                                  defaultValue={
                                    securityTypeBeingEdited?.securedUnsecured ??
                                    undefined
                                  }
                                  id="secured-unsecured-field"
                                  label="Secured Unsecured"
                                  name="secured-unsecured-field"
                                  options={data7.securedUnsecureds.map(su => ({
                                    label: su.name,
                                    value: su.value
                                  }))}
                                  placeholder="Select secured/unsecured..."
                                />
                              </div>
                              <div className="grid grid-cols-3 gap-x-2.5 gap-y-2.5">
                                <Field>
                                  <FieldLabel htmlFor="cash-flow-sweep-field">
                                    Cash Flow Sweep
                                  </FieldLabel>
                                  <InputGroup>
                                    <NumberField
                                      defaultValue={
                                        securityTypeBeingEdited?.cashFlowSweep ??
                                        undefined
                                      }
                                      id="cash-flow-sweep-field"
                                      max={100}
                                      min={0}
                                      name="cash-flow-sweep-field"
                                      placeholder="Enter Value"
                                    />
                                    <InputGroupAddon align="inline-end">
                                      <InputGroupText>%</InputGroupText>
                                    </InputGroupAddon>
                                  </InputGroup>
                                </Field>
                                {selectedSecurityCategory !== 'equity' && (
                                  <ComboboxField
                                    defaultValue={
                                      securityTypeBeingEdited?.lienSplit ??
                                      undefined
                                    }
                                    id="lien-split-field"
                                    label="Lien Split"
                                    name="lien-split-field"
                                    options={data7.lienSplits.map(ls => ({
                                      label: ls.name,
                                      value: ls.value
                                    }))}
                                    placeholder="Select lien split..."
                                  />
                                )}
                                <Field>
                                  <FieldLabel htmlFor="covenants-field">
                                    Covenants
                                  </FieldLabel>
                                  <Input
                                    defaultValue={
                                      securityTypeBeingEdited?.covenants ??
                                      undefined
                                    }
                                    id="covenants-field"
                                    name="covenants-field"
                                    placeholder="Enter Value"
                                  />
                                </Field>
                              </div>
                              <div className="grid grid-cols-3 gap-x-2.5 gap-y-2.5">
                                <Field>
                                  <FieldLabel htmlFor="cash-flow-sweep-details-field">
                                    Cash Flow Sweep Details
                                  </FieldLabel>
                                  <Textarea
                                    defaultValue={
                                      securityTypeBeingEdited?.cashFlowSweepDetails ??
                                      undefined
                                    }
                                    id="cash-flow-sweep-details-field"
                                    name="cash-flow-sweep-details-field"
                                    placeholder="Enter Value"
                                  />
                                </Field>
                                <ComboboxField
                                  defaultValue={
                                    securityTypeBeingEdited?.debtTranche ??
                                    undefined
                                  }
                                  id="debt-tranche-field"
                                  label="Debt Tranche"
                                  name="debt-tranche-field"
                                  options={data7.debtTranches.map(dt => ({
                                    label: dt.name,
                                    value: dt.value
                                  }))}
                                  placeholder="Select debt tranche..."
                                />
                                <Field>
                                  <FieldLabel htmlFor="cost-of-warrants-field">
                                    Cost of Warrants
                                  </FieldLabel>
                                  <NumberField
                                    defaultValue={
                                      securityTypeBeingEdited?.costOfWarrants ??
                                      undefined
                                    }
                                    id="cost-of-warrants-field"
                                    name="cost-of-warrants-field"
                                    placeholder="Enter Value"
                                  />
                                </Field>
                              </div>
                              <div className="grid grid-cols-3 gap-x-2.5 gap-y-2.5">
                                <ComboboxField
                                  defaultValue={
                                    securityTypeBeingEdited?.cashNoncash ??
                                    undefined
                                  }
                                  id="cash-non-cash-field"
                                  label="Cash Non-Cash"
                                  name="cash-non-cash-field"
                                  options={data7.cashNoncashes.map(cnc => ({
                                    label: cnc.name,
                                    value: cnc.value
                                  }))}
                                  placeholder="Select cash/non-cash..."
                                />
                                <Field>
                                  <FieldLabel htmlFor="security-description-field">
                                    Security Description
                                  </FieldLabel>
                                  <Textarea
                                    defaultValue={
                                      securityTypeBeingEdited?.description ??
                                      undefined
                                    }
                                    id="security-description-field"
                                    name="security-description-field"
                                    placeholder="Enter Value"
                                  />
                                </Field>
                                <Field orientation="horizontal">
                                  <FieldLabel htmlFor="warrants-attached-field">
                                    Warrants Attached?
                                  </FieldLabel>
                                  <Checkbox
                                    checked={warrantsAttached}
                                    id="warrants-attached-field"
                                    onCheckedChange={c => {
                                      setWarrantsAttached(c === true);
                                    }}
                                  />
                                </Field>
                                {selectedSecurityCategory ===
                                  'unsecured_debt' && (
                                  <ComboboxField
                                    defaultValue={
                                      securityTypeBeingEdited?.lienType ??
                                      undefined
                                    }
                                    id="lien-type-field"
                                    label="Lien Type"
                                    name="lien-type-field"
                                    options={data7.lienTypes.map(lt => ({
                                      label: lt.name,
                                      value: lt.value
                                    }))}
                                    placeholder="Select lien type..."
                                  />
                                )}
                              </div>
                            </FieldGroup>
                          </FieldSet>
                        </>
                      )}
                    </FieldGroup>
                  </form>
                </DialogBody>
                <DialogFooter>
                  <Button
                    disabled={
                      securityTypeBeingEdited
                        ? isUpdatingOpstreamDealOpportunitySecurity
                        : isCreatingOpstreamDealOpportunitySecurity
                    }
                    form="security-type-form"
                    type="submit"
                  >
                    {(securityTypeBeingEdited
                      ? isUpdatingOpstreamDealOpportunitySecurity
                      : isCreatingOpstreamDealOpportunitySecurity) && (
                      <Spinner />
                    )}
                    {securityTypeBeingEdited ? 'Save' : 'Add Row'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </SectionHeader>
          <div className="bg-card border-border rounded-md border p-4">
            <DataTable
              className="table-fixed"
              columns={securityTypeColumns}
              data={data2.opportunitySecurities}
              emptyAction={
                <Button
                  onClick={() => {
                    setSecurityTypeDialogIsOpen(true);
                  }}
                >
                  <PlusIcon />
                  Add Row
                </Button>
              }
              enableRowSelection
              getRowId={row => row.id}
              onRowClick={row => {
                setSelectedSecurityId(row.id);
              }}
              selectedRowId={selectedSecurityId}
            />
          </div>
        </div>

        <div className="min-w-0 space-y-2">
          <CashUseSection
            dealOpportunityId={id}
            onCashUseTotalChange={onCashUseTotalChange}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Dialog
          onOpenChange={setCreatePartyDialogIsOpen}
          open={createPartyDialogIsOpen}
        >
          <div className="flex h-9 items-center justify-between">
            <h2 className="text-lg font-semibold">Security Party</h2>
            {data3?.securityPartiesBySecurity &&
              data3.securityPartiesBySecurity.length !== 0 &&
              (isSecurityTypeSelected ? (
                <DialogTrigger asChild>
                  <Button>
                    <PlusIcon />
                    New Security Party
                  </Button>
                </DialogTrigger>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button disabled>
                        <PlusIcon />
                        New Security Party
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    Please select a row in the "Security Type" table
                  </TooltipContent>
                </Tooltip>
              ))}
          </div>
          {loading3 ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="size-6 text-blue-500" />
            </div>
          ) : !data3?.securityPartiesBySecurity ||
            data3.securityPartiesBySecurity.length === 0 ? (
            <Empty className="px-0! pt-5! pb-0!">
              <EmptyContent>
                {isSecurityTypeSelected ? (
                  <DialogTrigger asChild>
                    <Button>
                      <PlusIcon />
                      New Security Party
                    </Button>
                  </DialogTrigger>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">
                        <Button disabled>
                          <PlusIcon />
                          New Security Party
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Please select a row in the "Security Type" table
                    </TooltipContent>
                  </Tooltip>
                )}
              </EmptyContent>
            </Empty>
          ) : (
            <>
              <AlertDialog
                onOpenChange={setSecurityPartyDeleteDialogIsOpen}
                open={securityPartyDeleteDialogIsOpen}
              >
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      Delete security party row?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete{' '}
                      <span className="font-medium">
                        {securityPartyBeingDeleted?.name ?? 'this row'}
                      </span>
                      . This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isDeletingSecurityParty}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      disabled={
                        !securityPartyBeingDeleted || isDeletingSecurityParty
                      }
                      onClick={() => void handleConfirmDeleteSecurityParty()}
                      variant="destructive"
                    >
                      {isDeletingSecurityParty && <Spinner />}
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <div className="bg-card border-border rounded-md border p-4">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold">
                        Party
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Party Type
                      </TableHead>
                      <TableHead className="text-right text-xs font-semibold">
                        Drawn
                      </TableHead>
                      <TableHead className="text-right text-xs font-semibold">
                        Undrawn
                      </TableHead>
                      <TableHead className="text-xs font-semibold">
                        Syndicated
                      </TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data3.securityPartiesBySecurity.map(party => (
                      <TableRow key={party.id}>
                        <TableCell>{party.name}</TableCell>
                        <TableCell>
                          {getPartyTypeLabel(
                            party.partyType,
                            data6.getFieldSelections
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {party.drawnAmount != null
                            ? formatCurrencyValue(party.drawnAmount)
                            : null}
                        </TableCell>
                        <TableCell className="text-right">
                          {party.undrawnAmount != null
                            ? formatCurrencyValue(party.undrawnAmount)
                            : null}
                        </TableCell>
                        <TableCell>
                          {party.syndicatedPartner === null
                            ? null
                            : party.syndicatedPartner
                              ? 'Yes'
                              : 'No'}
                        </TableCell>
                        <TableCell className="w-0">
                          <div className="flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  className="size-8"
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  size="icon"
                                  type="button"
                                  variant="ghost"
                                >
                                  <EllipsisVertical className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onSelect={() => {
                                    setSecurityPartyBeingEdited(party);
                                    setEditPartyDialogIsOpen(true);
                                  }}
                                >
                                  <PenIcon />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={isDeletingSecurityParty}
                                  onSelect={() => {
                                    setSecurityPartyBeingDeleted(party);
                                    setSecurityPartyDeleteDialogIsOpen(true);
                                  }}
                                  variant="destructive"
                                >
                                  <Trash2Icon />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                Create Party
                <Badge variant="yellow">Unsaved</Badge>
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form
                id="create-party-form"
                noValidate
                onSubmit={e => void handleCreatePartyForm(e)}
              >
                <FieldGroup>
                  <div className="grid grid-cols-2 gap-x-2.5">
                    <Field>
                      <FieldLabel htmlFor="security-field">Security</FieldLabel>
                      <Input
                        defaultValue={getSecurityTypeLabel(
                          selectedSecurity?.securityType
                        )}
                        id="security-field"
                        readOnly
                      />
                    </Field>
                    <ComboboxField
                      id="party-field"
                      label="Party"
                      name="party-field"
                      options={data5.searchAccounts.map(account => ({
                        label: account.name ?? account.id,
                        value: account.id
                      }))}
                      placeholder="Select party..."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-x-2">
                    <Field>
                      <FieldLabel htmlFor="display-name-field">
                        Display Name
                      </FieldLabel>
                      <Input
                        id="display-name-field"
                        name="display-name-field"
                        placeholder="Enter Value"
                      />
                    </Field>
                    <ComboboxField
                      id="party-type-field"
                      label="Party Type"
                      name="party-type-field"
                      options={data6.getFieldSelections.map(pt => ({
                        label: pt.name,
                        value: pt.value
                      }))}
                      placeholder="Select party type..."
                    />
                  </div>
                  <Field orientation="horizontal">
                    <FieldLabel htmlFor="syndicated-partner-field">
                      Syndicated Partner?
                    </FieldLabel>
                    <Checkbox
                      id="syndicated-partner-field"
                      name="syndicated-partner-field"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="ownership-pct-field">
                      Ownership PCT
                    </FieldLabel>
                    <NumberField
                      id="ownership-pct-field"
                      name="ownership-pct-field"
                      placeholder="Enter Value"
                    />
                  </Field>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        Party Fundings
                      </span>
                      <Button
                        onClick={() => {
                          setAddFundingDialogIsOpen(true);
                        }}
                        size="sm"
                        type="button"
                      >
                        Add
                      </Button>
                    </div>
                    <div className="border-border rounded-md border">
                      <Table className="text-sm">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs font-semibold">
                              Funding amount
                            </TableHead>
                            {shouldShowUndrawnAmount && (
                              <TableHead className="text-xs font-semibold">
                                Undrawn amount
                              </TableHead>
                            )}
                            <TableHead className="text-xs font-semibold">
                              Funding date
                            </TableHead>
                            <TableHead />
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {createPartyFundings.length === 0 ? (
                            <TableRow>
                              <TableCell
                                className="text-muted-foreground text-center"
                                colSpan={shouldShowUndrawnAmount ? 4 : 3}
                              >
                                No fundings added yet
                              </TableCell>
                            </TableRow>
                          ) : (
                            createPartyFundings.map(funding => (
                              <TableRow key={funding.id}>
                                <TableCell>
                                  {formatCurrencyValue(funding.fundingAmount)}
                                </TableCell>
                                {shouldShowUndrawnAmount && (
                                  <TableCell>
                                    {formatCurrencyValue(funding.undrawnAmount)}
                                  </TableCell>
                                )}
                                <TableCell>
                                  {new Date(
                                    funding.fundingDate
                                  ).toLocaleDateString('en-US', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </TableCell>
                                <TableCell className="w-0">
                                  <Button
                                    className="size-8"
                                    onClick={() => {
                                      setCreatePartyFundings(prev =>
                                        prev.filter(f => f.id !== funding.id)
                                      );
                                    }}
                                    size="icon"
                                    type="button"
                                    variant="ghost"
                                  >
                                    <Trash2Icon className="size-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="text-sm font-medium">
                      Total Drawn Amount:{' '}
                      {formatCurrencyValue(
                        createPartyFundings.reduce(
                          (sum, f) => sum + f.fundingAmount,
                          0
                        )
                      )}
                    </div>
                  </div>
                </FieldGroup>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isCreatingSecurityParty || isCreatingPartyFunding}
                form="create-party-form"
                type="submit"
              >
                {(isCreatingSecurityParty || isCreatingPartyFunding) && (
                  <Spinner />
                )}
                Create Party
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Add Funding Dialog (Create Party) */}
      <Dialog
        onOpenChange={setAddFundingDialogIsOpen}
        open={addFundingDialogIsOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Funding</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              id="add-funding-form"
              noValidate
              onSubmit={e => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const fundingAmount = formData.get('funding-drawn-amount');
                const undrawnAmount = formData.get('funding-undrawn-amount');
                const fundingDate = formData.get('funding-date');
                if (!fundingAmount || !fundingDate) {
                  toast.error('Please fill in all required fields');
                  return;
                }
                const newDrawnAmount = Number(fundingAmount) || 0;
                const tempTotalDrawn = createPartyFundings.reduce(
                  (sum, f) => sum + f.fundingAmount,
                  0
                );
                const commitmentSize = selectedSecurity?.totalAmount ?? 0;
                const projectedTotal =
                  totalDrawnForSecurity + tempTotalDrawn + newDrawnAmount;
                if (commitmentSize > 0 && projectedTotal > commitmentSize) {
                  const availableAmount =
                    commitmentSize - totalDrawnForSecurity - tempTotalDrawn;
                  toast.error(
                    `Drawn amount exceeds remaining commitment. Available: ${formatCurrencyValue(availableAmount)}`
                  );
                  return;
                }
                const newFunding: TempFunding = {
                  fundingAmount: newDrawnAmount,
                  fundingDate: fundingDate as string,
                  id: crypto.randomUUID(),
                  undrawnAmount: Number(undrawnAmount) || 0
                };
                setCreatePartyFundings(prev => [...prev, newFunding]);
                setAddFundingDialogIsOpen(false);
              }}
            >
              <FieldGroup>
                <div
                  className={`grid gap-x-2.5 ${shouldShowUndrawnAmount ? 'grid-cols-2' : 'grid-cols-1'}`}
                >
                  <Field>
                    <FieldLabel htmlFor="funding-drawn-amount">
                      Drawn Amount <span className="text-red-500">*</span>
                    </FieldLabel>
                    <InputGroup>
                      <InputGroupAddon>
                        <InputGroupText>$</InputGroupText>
                      </InputGroupAddon>
                      <NumberField
                        defaultValue={
                          shouldShowUndrawnAmount
                            ? 0
                            : (() => {
                                const val = selectedSecurity?.totalAmount ?? 0;
                                return val % 1 !== 0 ? +val.toFixed(2) : val;
                              })()
                        }
                        id="funding-drawn-amount"
                        name="funding-drawn-amount"
                        placeholder="0.00"
                        step={0.01}
                      />
                    </InputGroup>
                  </Field>
                  {shouldShowUndrawnAmount && (
                    <Field>
                      <FieldLabel htmlFor="funding-undrawn-amount">
                        Undrawn Amount
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <NumberField
                          defaultValue={0}
                          id="funding-undrawn-amount"
                          name="funding-undrawn-amount"
                          placeholder="0"
                          step={0.01}
                        />
                      </InputGroup>
                    </Field>
                  )}
                </div>
                <Field>
                  <FieldLabel htmlFor="funding-date">Funding Date</FieldLabel>
                  <Input
                    defaultValue={new Date().toISOString().split('T')[0]}
                    id="funding-date"
                    name="funding-date"
                    type="date"
                  />
                </Field>
              </FieldGroup>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              form="add-funding-form"
              type="submit"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        onOpenChange={setEditPartyDialogIsOpen}
        open={editPartyDialogIsOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              Edit Party
              <Badge variant="yellow">Unsaved</Badge>
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              id="edit-party-form"
              noValidate
              onSubmit={e => void handleEditPartyForm(e)}
            >
              <FieldGroup>
                <div className="grid grid-cols-2 gap-x-2">
                  <Field>
                    <FieldLabel htmlFor="security-field">Security</FieldLabel>
                    <Input
                      defaultValue={getSecurityTypeLabel(
                        selectedSecurity?.securityType
                      )}
                      id="security-field"
                      readOnly
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="party-field">Party</FieldLabel>
                    <Input
                      defaultValue={securityPartyBeingEdited?.name ?? '-'}
                      disabled
                      id="party-field"
                      name="party-field"
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-x-2">
                  <Field>
                    <FieldLabel htmlFor="display-name-field">
                      Display Name
                    </FieldLabel>
                    <Input
                      defaultValue={securityPartyBeingEdited?.name ?? ''}
                      id="display-name-field"
                      name="display-name-field"
                      placeholder="Enter Value"
                    />
                  </Field>
                  <ComboboxField
                    defaultValue={securityPartyBeingEdited?.partyType ?? ''}
                    id="party-type-field"
                    label="Party Type"
                    name="party-type-field"
                    options={data6.getFieldSelections.map(pt => ({
                      label: pt.name,
                      value: pt.value
                    }))}
                    placeholder="Select party type..."
                  />
                </div>
                <Field orientation="horizontal">
                  <FieldLabel htmlFor="syndicated-partner-field">
                    Syndicated Partner?
                  </FieldLabel>
                  <Checkbox
                    defaultChecked={
                      securityPartyBeingEdited?.syndicatedPartner ?? false
                    }
                    id="syndicated-partner-field"
                    name="syndicated-partner-field"
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="ownership-pct-field">
                    Ownership PCT
                  </FieldLabel>
                  <NumberField
                    defaultValue={securityPartyBeingEdited?.fdoPct ?? undefined}
                    id="ownership-pct-field"
                    name="ownership-pct-field"
                    placeholder="Enter Value"
                  />
                </Field>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Party Fundings</span>
                    <Button
                      onClick={() => {
                        setEditAddFundingDialogIsOpen(true);
                      }}
                      size="sm"
                      type="button"
                    >
                      Add
                    </Button>
                  </div>
                  <div className="border-border rounded-md border">
                    <Table className="text-sm">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-semibold">
                            Funding amount
                          </TableHead>
                          {shouldShowUndrawnAmount && (
                            <TableHead className="text-xs font-semibold">
                              Undrawn amount
                            </TableHead>
                          )}
                          <TableHead className="text-xs font-semibold">
                            Funding date
                          </TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {!securityPartyBeingEdited?.fundingHistory ||
                        securityPartyBeingEdited.fundingHistory.length === 0 ? (
                          <TableRow>
                            <TableCell
                              className="text-muted-foreground text-center"
                              colSpan={shouldShowUndrawnAmount ? 4 : 3}
                            >
                              No fundings yet
                            </TableCell>
                          </TableRow>
                        ) : (
                          securityPartyBeingEdited.fundingHistory.map(
                            funding => (
                              <TableRow key={funding.id}>
                                <TableCell>
                                  {formatCurrencyValue(
                                    funding.fundingAmount ?? 0
                                  )}
                                </TableCell>
                                {shouldShowUndrawnAmount && (
                                  <TableCell>
                                    {formatCurrencyValue(
                                      funding.undrawnAmount ?? 0
                                    )}
                                  </TableCell>
                                )}
                                <TableCell>
                                  {funding.fundingDate
                                    ? new Date(
                                        funding.fundingDate
                                      ).toLocaleDateString('en-US', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      })
                                    : '-'}
                                </TableCell>
                                <TableCell className="w-0">
                                  <Button
                                    className="size-8"
                                    disabled={isDeletingPartyFunding}
                                    onClick={() =>
                                      void (async () => {
                                        try {
                                          await deletePartyFunding({
                                            refetchQueries: [
                                              {
                                                query:
                                                  GET_SECURITY_PARTIES_BY_SECURITY,
                                                variables: {
                                                  security_id:
                                                    selectedSecurityId
                                                }
                                              }
                                            ],
                                            variables: { id: funding.id }
                                          });
                                          toast.success('Funding deleted');
                                        } catch (err) {
                                          console.error(err);
                                          toast.error(
                                            'Failed to delete funding'
                                          );
                                        }
                                      })()
                                    }
                                    size="icon"
                                    type="button"
                                    variant="ghost"
                                  >
                                    <Trash2Icon className="size-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            )
                          )
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="text-sm font-medium">
                    Total Drawn Amount:{' '}
                    {formatCurrencyValue(
                      (securityPartyBeingEdited?.fundingHistory ?? []).reduce(
                        (sum, f) => sum + (f.fundingAmount ?? 0),
                        0
                      )
                    )}
                  </div>
                </div>
              </FieldGroup>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              disabled={isUpdatingSecurityParty}
              form="edit-party-form"
              type="submit"
            >
              {isUpdatingSecurityParty && <Spinner />}
              Edit Party
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Funding Dialog for Edit Mode */}
      <Dialog
        onOpenChange={setEditAddFundingDialogIsOpen}
        open={editAddFundingDialogIsOpen}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add Funding</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              id="edit-add-funding-form"
              noValidate
              onSubmit={e =>
                void (async () => {
                  e.preventDefault();
                  if (!securityPartyBeingEdited) return;
                  const formData = new FormData(e.currentTarget);
                  const fundingAmount = formData.get(
                    'edit-funding-drawn-amount'
                  );
                  const undrawnAmount = formData.get(
                    'edit-funding-undrawn-amount'
                  );
                  const fundingDate = formData.get('edit-funding-date');
                  if (!fundingAmount) {
                    toast.error('Please fill in the drawn amount');
                    return;
                  }
                  const newDrawnAmount = Number(fundingAmount) || 0;
                  const commitmentSize = selectedSecurity?.totalAmount ?? 0;
                  const projectedTotal = totalDrawnForSecurity + newDrawnAmount;
                  if (commitmentSize > 0 && projectedTotal > commitmentSize) {
                    toast.error(
                      `Drawn amount exceeds remaining commitment. Available: ${formatCurrencyValue(remainingCommitment)}`
                    );
                    return;
                  }
                  try {
                    await createPartyFunding({
                      refetchQueries: [
                        {
                          query: GET_SECURITY_PARTIES_BY_SECURITY,
                          variables: { security_id: selectedSecurityId }
                        },
                        {
                          query: GET_OPPORTUNITY_SECURITIES,
                          variables: { deal_id: id }
                        }
                      ],
                      variables: {
                        values: {
                          fundingAmount: newDrawnAmount,
                          fundingDate: fundingDate ?? null,
                          securityPartyId: securityPartyBeingEdited.id,
                          undrawnAmount: Number(undrawnAmount) || 0
                        }
                      }
                    });
                    toast.success('Funding added');
                    setEditAddFundingDialogIsOpen(false);
                  } catch (err) {
                    console.error(err);
                    toast.error('Failed to add funding');
                  }
                })()
              }
            >
              <FieldGroup>
                <div
                  className={`grid gap-x-2.5 ${shouldShowUndrawnAmount ? 'grid-cols-2' : 'grid-cols-1'}`}
                >
                  <Field>
                    <FieldLabel htmlFor="edit-funding-drawn-amount">
                      Drawn Amount <span className="text-red-500">*</span>
                    </FieldLabel>
                    <InputGroup>
                      <InputGroupAddon>
                        <InputGroupText>$</InputGroupText>
                      </InputGroupAddon>
                      <NumberField
                        defaultValue={
                          shouldShowUndrawnAmount
                            ? 0
                            : remainingCommitment > 0
                              ? remainingCommitment % 1 !== 0
                                ? +remainingCommitment.toFixed(2)
                                : remainingCommitment
                              : 0
                        }
                        id="edit-funding-drawn-amount"
                        name="edit-funding-drawn-amount"
                        placeholder="0.00"
                        step={0.01}
                      />
                    </InputGroup>
                  </Field>
                  {shouldShowUndrawnAmount && (
                    <Field>
                      <FieldLabel htmlFor="edit-funding-undrawn-amount">
                        Undrawn Amount
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <NumberField
                          defaultValue={0}
                          id="edit-funding-undrawn-amount"
                          name="edit-funding-undrawn-amount"
                          placeholder="0"
                          step={0.01}
                        />
                      </InputGroup>
                    </Field>
                  )}
                </div>
                <Field>
                  <FieldLabel htmlFor="edit-funding-date">
                    Funding Date
                  </FieldLabel>
                  <Input
                    defaultValue={new Date().toISOString().split('T')[0]}
                    id="edit-funding-date"
                    name="edit-funding-date"
                    type="date"
                  />
                </Field>
              </FieldGroup>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              disabled={isCreatingPartyFunding}
              form="edit-add-funding-form"
              type="submit"
            >
              {isCreatingPartyFunding && <Spinner />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
